/**
 * Угол между двумя стенами в узле — понятная людям замена координат X и Y угла.
 *
 * Угол меряем между стенами, от 0 до 180°: у прямоугольной комнаты в каждом углу 90°.
 * Задали угол — одна из двух стен поворачивается вокруг узла, сохраняя длину; её дальний
 * конец переезжает. Поворачиваем ту, у которой дальний конец меньше держится за план
 * (к нему примыкает меньше стен), при равенстве — более короткую: так правка задевает
 * чертёж меньше всего. Угол есть только там, где сходятся ровно две стены.
 */
import { dist } from "@/shared/lib/geometry";
import type { WallGraph } from "@/shared/plan";

/** Стена, выходящая из узла: её дальний конец и длина. */
interface CornerArm {
	farId: string;
	length: number;
	/** Сколько стен держит дальний конец, считая эту */
	farDegree: number;
	direction: { x: number; y: number };
}

const degreeOf = (graph: WallGraph, nodeId: string): number =>
	Object.values(graph.walls).filter((wall) => wall.a === nodeId || wall.b === nodeId).length;

/** Две стены угла; null — в узле сходится не две стены или они нулевой длины. */
const cornerArms = (graph: WallGraph, nodeId: string): [CornerArm, CornerArm] | null => {
	const node = graph.nodes[nodeId];
	if (!node) return null;
	const walls = Object.values(graph.walls).filter((wall) => wall.a === nodeId || wall.b === nodeId);
	if (walls.length !== 2) return null;
	const arms = walls.map((wall): CornerArm | null => {
		const farId = wall.a === nodeId ? wall.b : wall.a;
		const far = graph.nodes[farId];
		if (!far) return null;
		const length = dist(node, far);
		if (length < 1) return null;
		return {
			farId,
			length,
			farDegree: degreeOf(graph, farId),
			direction: { x: (far.x - node.x) / length, y: (far.y - node.y) / length },
		};
	});
	const [first, second] = arms;
	return first && second ? [first, second] : null;
};

/** Угол между двумя стенами в узле, градусы 0..180; null — угла нет (не две стены). */
export const cornerAngle = (graph: WallGraph, nodeId: string): number | null => {
	const arms = cornerArms(graph, nodeId);
	if (!arms) return null;
	const [a, b] = arms;
	const cos = Math.max(
		-1,
		Math.min(1, a.direction.x * b.direction.x + a.direction.y * b.direction.y),
	);
	return (Math.acos(cos) * 180) / Math.PI;
};

/**
 * Куда переехать дальнему концу одной из стен, чтобы угол в узле стал `degrees`.
 * null — угла нет. Поворот — в ту же сторону, куда стена уже отходит от соседней.
 */
export const cornerAngleMove = (
	graph: WallGraph,
	nodeId: string,
	degrees: number,
): { nodeId: string; x: number; y: number } | null => {
	const node = graph.nodes[nodeId];
	const arms = cornerArms(graph, nodeId);
	if (!node || !arms) return null;
	const [first, second] = arms;
	// Крутим ту, что меньше держится за план; при равенстве — короче
	const rotateFirst =
		first.farDegree !== second.farDegree
			? first.farDegree < second.farDegree
			: first.length < second.length;
	const fixed = rotateFirst ? second : first;
	const moving = rotateFirst ? first : second;
	const cross = fixed.direction.x * moving.direction.y - fixed.direction.y * moving.direction.x;
	const sign = cross < 0 ? -1 : 1;
	const angle = ((Math.max(1, Math.min(179, degrees)) * Math.PI) / 180) * sign;
	const cos = Math.cos(angle);
	const sin = Math.sin(angle);
	const direction = {
		x: fixed.direction.x * cos - fixed.direction.y * sin,
		y: fixed.direction.x * sin + fixed.direction.y * cos,
	};
	return {
		nodeId: moving.farId,
		x: Math.round(node.x + direction.x * moving.length),
		y: Math.round(node.y + direction.y * moving.length),
	};
};
